import type { ChakraProps } from "@chakra-ui/react"
import { Box, Flex, Text } from "@chakra-ui/react"
import {
  CleanBeautyIcon,
  CrueltyFreeIcon,
  FragranceFreeIcon,
  FullyRecyclableIcon,
  PartlyRecyclableIcon,
  PCRPIRIcon,
  VeganIcon,
} from "components/SvgIcons"
import { PlasticNeutralIcon } from "components/SvgIcons/icons/plastic-neutral"
import { useScreenViewport } from "lib/checkViewport"
import React, { Fragment } from "react"
import { space } from "theme/themeConfig"
import type {
  ProductPricingType,
  VariantStandardsType,
} from "@shared/graphql.types"
import { colours } from "@shared/ui.design-system"

type Props = ChakraProps & {
  standards: VariantStandardsType
  pricing?: ProductPricingType
}

export const Standards: React.FC<Props> = ({ standards, ...otherProps }) => {
  const { isTiny } = useScreenViewport()

  const standardsMap = [
    {
      id: "vegan",
      label: "Vegan",
      icon: <VeganIcon pr={-1} boxSize="1.75rem" />,
    },
    {
      id: "cleanBeauty",
      label: "Clean Beauty",
      icon: <CleanBeautyIcon pr={-1} boxSize="1.75rem" />,
    },
    {
      id: "crueltyFree",
      label: "Cruelty Free",
      icon: <CrueltyFreeIcon pr={-1} boxSize="1.75rem" />,
    },
    {
      id: "fragranceFree",
      label: "Fragrance Free",
      icon: <FragranceFreeIcon pr={-1} boxSize="1.75rem" />,
    },
    {
      id: "fullyRecyclable",
      label: "Fully Recyclable",
      icon: <FullyRecyclableIcon pr={-1} boxSize="1.75rem" />,
    },
    {
      id: "partlyRecyclable",
      label: "Partly Recyclable",
      icon: <PartlyRecyclableIcon pr={-1} boxSize="1.75rem" />,
    },
    {
      id: "pcrpir",
      label: "Contains Recycled Materials",
      icon: <PCRPIRIcon pr={-1} boxSize="1.75rem" />,
    },
    {
      // The icon is not available yet.
      id: "dermatologistApproved",
      label: "Dermatologically Tested",
      icon: "",
    },
    {
      id: "clinicallyTested",
      label: "Clinically Tested",
      icon: "",
    },
    {
      id: "plasticNeutral",
      label: "Plastic Neutral",
      icon: <PlasticNeutralIcon pr={-1} boxSize="1.75rem" />,
    },
  ]
  const standardsLabels = Object.entries(standards)
    .filter(([, flag]) => flag === true)
    .map(([label]) => label)

  const filteredStandards = standardsMap.filter((item) =>
    standardsLabels.includes(item.id)
  )

  return (
    <Flex align="center" {...otherProps} wrap="wrap">
      {filteredStandards.map((item, i) => (
        <Fragment key={item.id}>
          <Flex align="center" justify="center" py={space[-6]}>
            {item.icon}
            <Text variant="acuminLight0" mt={0}>
              {item.label}
            </Text>
          </Flex>
          {i !== filteredStandards.length - 1 && (
            <Box
              width="0.5px"
              backgroundColor={colours.secondary.deep}
              height={space[1.5]}
              mx={isTiny ? -2 : { base: 1, md: 2 }}
            />
          )}
        </Fragment>
      ))}
    </Flex>
  )
}
